import {marked} from 'marked';
import * as DOMPurifyModule from 'dompurify';

// Handle default export properly
const DOMPurify = (DOMPurifyModule as any).default || DOMPurifyModule;

/**
 * Markdown Helper
 *
 * Utility class for rendering markdown content to HTML with sanitization.
 * Uses marked.js for parsing and DOMPurify for security.
 */
export class MarkdownHelper {

    /**
     * Configure marked with custom options
     */
    private static configureMarked(): void {
        // Configure marked extensions for heading IDs
        marked.use({
            gfm: true,
            breaks: true,
        });

        // Use custom renderer to add IDs to headings
        const renderer = new marked.Renderer();

        renderer.heading = function({tokens, depth}) {
            const text = this.parser.parseInline(tokens);

            // Generate clean ID from heading text
            const headingId = text
                .toLowerCase()
                .replace(/[^\w\s-]/g, '')  // Remove special characters
                .replace(/\s+/g, '-')       // Replace spaces with hyphens
                .replace(/-+/g, '-')        // Replace multiple hyphens with single
                .replace(/^-|-$/g, '');     // Remove leading/trailing hyphens

            // Debug: Log generated IDs
            console.log('[Markdown] Generated heading ID:', headingId, 'for text:', text);

            return `<h${depth} id="${headingId}">${text}</h${depth}>`;
        };

        marked.setOptions({
            renderer: renderer,
            gfm: true,
            breaks: true,
            pedantic: false,
        });
    }

    /**
     * Render markdown content to sanitized HTML
     * @param markdown The markdown content to render
     * @returns Sanitized HTML string
     */
    static async renderMarkdown(markdown: string | null | undefined): Promise<string> {
        if (!markdown || markdown.trim() === '') {
            return '<p class="text-color-secondary">No content available</p>';
        }

        try {
            // Configure marked
            this.configureMarked();

            // Parse markdown to HTML
            const rawHtml = await marked.parse(markdown);

            // Sanitize HTML to prevent XSS attacks
            const sanitizedHtml = this.sanitizeHtml(rawHtml);

            return sanitizedHtml;
        } catch (error) {
            console.error('Error rendering markdown:', error);
            return `<p class="text-color-secondary">Error rendering markdown content</p>`;
        }
    }

    /**
     * Render markdown synchronously (for non-async contexts)
     * @param markdown The markdown content to render
     * @returns Sanitized HTML string
     */
    static renderMarkdownSync(markdown: string | null | undefined): string {
        if (!markdown || markdown.trim() === '') {
            return '<p class="text-color-secondary">No content available</p>';
        }

        try {
            // Configure marked
            this.configureMarked();

            // Parse markdown to HTML (sync version)
            const rawHtml = marked.parse(markdown) as string;

            // Sanitize HTML
            const sanitizedHtml = this.sanitizeHtml(rawHtml);

            return sanitizedHtml;
        } catch (error) {
            console.error('Error rendering markdown:', error);
            return `<p class="text-color-secondary">Error rendering markdown content</p>`;
        }
    }

    /**
     * Sanitize HTML to prevent XSS attacks
     * @param html The HTML to sanitize
     * @returns Sanitized HTML
     */
    private static sanitizeHtml(html: string): string {
        // Debug: Count headings and check for IDs
        const headingMatches = html.match(/<h[1-6][^>]*>/g) || [];
        const headingsWithId = html.match(/<h[1-6][^>]*id="[^"]*"/g) || [];
        console.log('[DOMPurify] Total headings found:', headingMatches.length);
        console.log('[DOMPurify] Headings with ID:', headingsWithId.length);
        if (headingMatches.length > 0) {
            console.log('[DOMPurify] First heading:', headingMatches[0]);
            console.log('[DOMPurify] HTML start (first 800 chars):', html.substring(0, 800));
        }

        // Configure DOMPurify with ADD_ATTR to ensure id is preserved
        const config = {
            ALLOWED_TAGS: [
                'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                'p', 'br', 'hr',
                'strong', 'em', 'b', 'i', 'u', 's', 'del', 'ins',
                'ul', 'ol', 'li',
                'a', 'img',
                'blockquote', 'pre', 'code',
                'table', 'thead', 'tbody', 'tr', 'th', 'td',
                'div', 'span'
            ],
            ALLOWED_ATTR: [
                'href', 'title', 'target', 'rel',
                'src', 'alt', 'width', 'height',
                'class', 'id',
                'align', 'valign'
            ],
            ADD_ATTR: ['id'], // Explicitly add id attribute
            ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
            KEEP_CONTENT: true,
            RETURN_TRUSTED_TYPE: false
        };

        const sanitized = DOMPurify.sanitize(html, config);

        // Debug: Check after sanitization
        const sanitizedHeadingsWithId = sanitized.match(/<h[1-6][^>]*id="[^"]*"/g) || [];
        console.log('[DOMPurify] After sanitization - Headings with ID:', sanitizedHeadingsWithId.length);
        if (sanitizedHeadingsWithId.length > 0) {
            console.log('[DOMPurify] First sanitized heading with ID:', sanitizedHeadingsWithId[0]);
        } else if (headingsWithId.length > 0) {
            console.warn('[DOMPurify] WARNING: IDs were stripped during sanitization!');
        }

        return sanitized;
    }

    /**
     * Extract plain text from markdown (strips all formatting)
     * @param markdown The markdown content
     * @returns Plain text
     */
    static extractPlainText(markdown: string | null | undefined): string {
        if (!markdown) {
            return '';
        }

        // Remove markdown syntax
        let text = markdown;

        // Remove headers
        text = text.replace(/^#{1,6}\s+/gm, '');

        // Remove bold/italic
        text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
        text = text.replace(/\*([^*]+)\*/g, '$1');
        text = text.replace(/__([^_]+)__/g, '$1');
        text = text.replace(/_([^_]+)_/g, '$1');

        // Remove links but keep text
        text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

        // Remove images
        text = text.replace(/!\[([^\]]*)\]\([^)]+\)/g, '');

        // Remove code blocks
        text = text.replace(/```[\s\S]*?```/g, '');

        // Remove inline code
        text = text.replace(/`([^`]+)`/g, '$1');

        // Remove blockquotes
        text = text.replace(/^>\s+/gm, '');

        // Remove list markers
        text = text.replace(/^[\s]*[-*+]\s+/gm, '');
        text = text.replace(/^[\s]*\d+\.\s+/gm, '');

        return text.trim();
    }

    /**
     * Get a preview/excerpt from markdown content
     * @param markdown The markdown content
     * @param maxLength Maximum length of preview
     * @returns Preview text
     */
    static getPreview(markdown: string | null | undefined, maxLength: number = 200): string {
        const plainText = this.extractPlainText(markdown);

        if (plainText.length <= maxLength) {
            return plainText;
        }

        return plainText.substring(0, maxLength).trim() + '...';
    }

    /**
     * Check if markdown contains specific syntax
     * @param markdown The markdown content
     * @param syntax The syntax to check for ('headers', 'links', 'images', 'code', 'lists')
     * @returns true if syntax is found
     */
    static containsSyntax(markdown: string | null | undefined, syntax: 'headers' | 'links' | 'images' | 'code' | 'lists'): boolean {
        if (!markdown) {
            return false;
        }

        const patterns: { [key: string]: RegExp } = {
            headers: /^#{1,6}\s+/gm,
            links: /\[([^\]]+)\]\(([^)]+)\)/,
            images: /!\[([^\]]*)\]\(([^)]+)\)/,
            code: /```[\s\S]*?```|`[^`]+`/,
            lists: /^[\s]*[-*+]\s+|^[\s]*\d+\.\s+/gm
        };

        return patterns[syntax]?.test(markdown) || false;
    }

    /**
     * Convert HTML to Markdown (basic conversion)
     * Note: This is a simple conversion and may not handle all HTML cases
     * @param html The HTML content
     * @returns Markdown string
     */
    static htmlToMarkdown(html: string | null | undefined): string {
        if (!html) {
            return '';
        }

        let markdown = html;

        // Remove HTML comments
        markdown = markdown.replace(/<!--[\s\S]*?-->/g, '');

        // Convert headers
        markdown = markdown.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n');
        markdown = markdown.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n');
        markdown = markdown.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n');
        markdown = markdown.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1\n\n');
        markdown = markdown.replace(/<h5[^>]*>(.*?)<\/h5>/gi, '##### $1\n\n');
        markdown = markdown.replace(/<h6[^>]*>(.*?)<\/h6>/gi, '###### $1\n\n');

        // Convert bold and italic
        markdown = markdown.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**');
        markdown = markdown.replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**');
        markdown = markdown.replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*');
        markdown = markdown.replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*');

        // Convert links
        markdown = markdown.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)');

        // Convert images
        markdown = markdown.replace(/<img[^>]*src="([^"]*)"[^>]*alt="([^"]*)"[^>]*>/gi, '![$2]($1)');
        markdown = markdown.replace(/<img[^>]*src="([^"]*)"[^>]*>/gi, '![]($1)');

        // Convert line breaks
        markdown = markdown.replace(/<br[\s]*\/?>/gi, '\n');

        // Convert paragraphs
        markdown = markdown.replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n');

        // Convert code
        markdown = markdown.replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`');
        markdown = markdown.replace(/<pre[^>]*>(.*?)<\/pre>/gi, '```\n$1\n```\n');

        // Convert lists
        markdown = markdown.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (match, content) => {
            return content.replace(/<li[^>]*>(.*?)<\/li>/gi, '* $1\n');
        });
        markdown = markdown.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (match, content) => {
            let counter = 1;
            return content.replace(/<li[^>]*>(.*?)<\/li>/gi, () => {
                return `${counter++}. $1\n`;
            });
        });

        // Remove remaining HTML tags
        markdown = markdown.replace(/<[^>]+>/g, '');

        // Clean up excessive newlines
        markdown = markdown.replace(/\n{3,}/g, '\n\n');

        return markdown.trim();
    }
}
